import React, { useState, useRef, useCallback } from "react";
import { apiPost } from "../../services/api";
import "../styles/AddBlockForm.css";
import ImageUploader from "./ImageUploader";
import { useNotification } from "../../contexts/NotificationContext";

// Word-подобный Rich Text редактор
const WordLikeEditor = ({ value, onChange, placeholder, height = 200 }) => {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState({});

  // Применить команду форматирования
  const execCommand = useCallback(
    (command, value = null) => {
      document.execCommand(command, false, value);
      editorRef.current?.focus();

      // Обновляем состояние активных форматов
      updateActiveFormats();

      // Сохраняем изменения
      if (onChange) {
        setTimeout(() => {
          onChange(editorRef.current?.innerHTML || "");
        }, 10);
      }
    },
    [onChange]
  );

  // Обновляем состояние активных форматов
  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
    });
  }, []);

  // Обработка изменений в редакторе
  const handleInput = useCallback(() => {
    if (onChange) {
      onChange(editorRef.current?.innerHTML || "");
    }
    updateActiveFormats();
  }, [onChange, updateActiveFormats]);

  // Обработка нажатий клавиш
  const handleKeyDown = useCallback(
    (e) => {
      // Ctrl+B для жирного
      if (e.ctrlKey && e.key === "b") {
        e.preventDefault();
        execCommand("bold");
      }
      // Ctrl+I для курсива
      if (e.ctrlKey && e.key === "i") {
        e.preventDefault();
        execCommand("italic");
      }
      // Ctrl+U для подчеркивания
      if (e.ctrlKey && e.key === "u") {
        e.preventDefault();
        execCommand("underline");
      }
    },
    [execCommand]
  );

  // Применить цвет к выделенному тексту
  const applyColor = useCallback(
    (color) => {
      execCommand("foreColor", color);
    },
    [execCommand]
  );

  // Применить цвет фона
  const applyBackgroundColor = useCallback(
    (color) => {
      execCommand("backColor", color);
    },
    [execCommand]
  );

  // Применить размер шрифта
  const applyFontSize = useCallback(
    (size) => {
      const sizeMap = {
        "12px": "1",
        "14px": "2",
        "16px": "3",
        "18px": "4",
        "24px": "5",
        "32px": "6",
        "48px": "7",
      };
      execCommand("fontSize", sizeMap[size] || "3");
    },
    [execCommand]
  );

  // Установить содержимое
  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  return (
    <div className="word-like-editor">
      {/* Панель инструментов */}
      <div className="editor-toolbar">
        {/* Основное форматирование */}
        <div className="toolbar-group">
          <button
            type="button"
            className={`toolbar-btn ${activeFormats.bold ? "active" : ""}`}
            onClick={() => execCommand("bold")}
            title="Жирный (Ctrl+B)"
          >
            <strong>Ж</strong>
          </button>

          <button
            type="button"
            className={`toolbar-btn ${activeFormats.italic ? "active" : ""}`}
            onClick={() => execCommand("italic")}
            title="Курсив (Ctrl+I)"
          >
            <em>К</em>
          </button>

          <button
            type="button"
            className={`toolbar-btn ${activeFormats.underline ? "active" : ""}`}
            onClick={() => execCommand("underline")}
            title="Подчеркивание (Ctrl+U)"
          >
            <u>П</u>
          </button>
        </div>

        <div className="toolbar-separator"></div>

        {/* Размер шрифта */}
        <div className="toolbar-group">
          <select
            className="font-size-select"
            onChange={(e) => applyFontSize(e.target.value)}
            defaultValue="16px"
          >
            <option value="12px">12px</option>
            <option value="14px">14px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="24px">24px</option>
            <option value="32px">32px</option>
            <option value="48px">48px</option>
          </select>
        </div>

        <div className="toolbar-separator"></div>

        {/* Цвета текста */}
        <div className="toolbar-group">
          <span className="group-label">Цвет:</span>
          {[
            "#000000",
            "#ff0000",
            "#00aa00",
            "#0066cc",
            "#ff8800",
            "#8800cc",
            "#cc0088",
            "#666666",
            "#004D40"
          ].map((color) => (
            <button
              key={color}
              type="button"
              className="color-btn"
              style={{ backgroundColor: color }}
              onClick={() => applyColor(color)}
              title={`Цвет текста: ${color}`}
            >
              А
            </button>
          ))}
        </div>

        <div className="toolbar-separator"></div>

        {/* Цвета фона */}
        <div className="toolbar-group">
          <span className="group-label">Фон:</span>
          {[
            "transparent",
            "#ffff00",
            "#00ffff",
            "#ff00ff",
            "#90EE90",
            "#FFB6C1",
            "#F0E68C",
            "#DDA0DD",
          ].map((color) => (
            <button
              key={color}
              type="button"
              className="bg-color-btn"
              style={{
                backgroundColor: color === "transparent" ? "white" : color,
                border: color === "transparent" ? "2px solid #ccc" : "none",
              }}
              onClick={() => applyBackgroundColor(color)}
              title={`Цвет фона: ${color}`}
            >
              {color === "transparent" ? "∅" : "Ф"}
            </button>
          ))}
        </div>

        <div className="toolbar-separator"></div>

        {/* Выравнивание */}
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("justifyLeft")}
            title="По левому краю"
          >
            ⬅
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("justifyCenter")}
            title="По центру"
          >
            ⬌
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("justifyRight")}
            title="По правому краю"
          >
            ➡
          </button>
        </div>

        <div className="toolbar-separator"></div>

        {/* Списки */}
        <div className="toolbar-group">
          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("insertUnorderedList")}
            title="Маркированный список"
          >
            • Список
          </button>

          <button
            type="button"
            className="toolbar-btn"
            onClick={() => execCommand("insertOrderedList")}
            title="Нумерованный список"
          >
            1. Список
          </button>
        </div>
      </div>

      {/* Редактор */}
      <div
        ref={editorRef}
        className="rich-editor-content"
        contentEditable
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onMouseUp={updateActiveFormats}
        onKeyUp={updateActiveFormats}
        style={{ height: `${height}px` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />
    </div>
  );
};

// Компонент для Rich Text полей с переводами (используется для всех текстов)
const EnhancedTextInput = ({
  fieldName,
  label,
  required = false,
  blockContent,
  setBlockContent,
  currentLanguage,
  setCurrentLanguage,
  languages,
  height = 200,
}) => {
  const currentValue = blockContent[fieldName]?.[currentLanguage] || "";

  const handleChange = (value) => {
    setBlockContent((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [currentLanguage]: value,
      },
    }));
  };

  return (
    <div className="form-group enhanced-text-group">
      <label>{label}:</label>

      {/* Переключатель языков */}
      <div className="language-tabs">
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`language-tab ${currentLanguage === lang.code ? "active" : ""
              }`}
            onClick={() => setCurrentLanguage(lang.code)}
          >
            {lang.name}
          </button>
        ))}
      </div>

      {/* Word-подобный редактор */}
      <WordLikeEditor
        value={currentValue}
        onChange={handleChange}
        placeholder={`Введите ${label.toLowerCase()} на ${languages.find((l) => l.code === currentLanguage)?.name
          }`}
        height={height}
      />

      {/* Индикатор заполненности переводов */}
      <div className="translation-indicators">
        {languages.map((lang) => (
          <span
            key={lang.code}
            className={`translation-indicator ${blockContent[fieldName]?.[lang.code] ? "filled" : "empty"
              }`}
            title={`${lang.name}: ${blockContent[fieldName]?.[lang.code] ? "заполнено" : "пусто"
              }`}
          >
            {lang.code.toUpperCase()}
          </span>
        ))}
      </div>
    </div>
  );
};

// Простой компонент для полей без форматирования (например, URL)
const SimpleInput = ({
  fieldName,
  label,
  type = "text",
  required = false,
  blockContent,
  setBlockContent,
  currentLanguage,
  setCurrentLanguage,
  languages,
}) => {
  const currentValue = blockContent[fieldName]?.[currentLanguage] || "";

  const handleChange = (value) => {
    setBlockContent((prev) => ({
      ...prev,
      [fieldName]: {
        ...prev[fieldName],
        [currentLanguage]: value,
      },
    }));
  };

  return (
    <div className="form-group">
      <label>{label}:</label>

      <div className="language-tabs">
        {languages.map((lang) => (
          <button
            key={lang.code}
            type="button"
            className={`language-tab ${currentLanguage === lang.code ? "active" : ""
              }`}
            onClick={() => setCurrentLanguage(lang.code)}
          >
            {lang.name}
          </button>
        ))}
      </div>

      <input
        type={type}
        value={currentValue}
        onChange={(e) => handleChange(e.target.value)}
        required={required}
        placeholder={`Введите ${label.toLowerCase()} на ${languages.find((l) => l.code === currentLanguage)?.name
          }`}
      />

      <div className="translation-indicators">
        {languages.map((lang) => (
          <span
            key={lang.code}
            className={`translation-indicator ${blockContent[fieldName]?.[lang.code] ? "filled" : "empty"
              }`}
            title={`${lang.name}: ${blockContent[fieldName]?.[lang.code] ? "заполнено" : "пусто"
              }`}
          >
            {lang.code.toUpperCase()}
          </span>
        ))}
      </div>
    </div>
  );
};

const AddBlockForm = ({ pageId, onBlockAdded }) => {
  const [blockType, setBlockType] = useState("TextBlock");
  const [blockContent, setBlockContent] = useState({});
  const [order, setOrder] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentLanguage, setCurrentLanguage] = useState("kz");
  const { showNotification } = useNotification();

  const languages = [
    { code: "kz", name: "Қазақша" },
    { code: "ru", name: "Русский" },
    { code: "en", name: "English" },
  ];

  const handleImageUploaded = (imageData) => {
    if (blockType === "ImageBlock") {
      const currentImages = blockContent.images || [];
      setBlockContent({
        ...blockContent,
        images: [...currentImages, ...imageData],
      });
    } else if (blockType === "TextImageBlock") {
      setBlockContent({
        ...blockContent,
        imageUrl: imageData.imageUrl,
        altText: { [currentLanguage]: imageData.altText || "" },
      });
    }
  };

  const renderContentFields = () => {
    switch (blockType) {
      case "TitleBlock":
        return (
          <>
            <EnhancedTextInput
              fieldName="title"
              label="Заголовок"
              required={false}
              blockContent={blockContent}
              setBlockContent={setBlockContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={150}
            />
            <EnhancedTextInput
              fieldName="subtitle"
              label="Подзаголовок"
              required={false}
              blockContent={blockContent}
              setBlockContent={setBlockContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={120}
            />
          </>
        );

      case "TextBlock":
        return (
          <EnhancedTextInput
            fieldName="text"
            label="Текст"
            required
            blockContent={blockContent}
            setBlockContent={setBlockContent}
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
            languages={languages}
            height={250}
          />
        );

      case "TextImageBlock":
        return (
          <>
            <EnhancedTextInput
              fieldName="text"
              label="Текст"
              required
              blockContent={blockContent}
              setBlockContent={setBlockContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={200}
            />

            <div className="form-group">
              <label>Изображение:</label>
              <ImageUploader onImageUploaded={handleImageUploaded} />
              {blockContent.imageUrl && (
                <div className="current-image">
                  <img
                    src={`${process.env.REACT_APP_API_URL}${blockContent.imageUrl}`}
                    alt="Uploaded image"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setBlockContent({
                        ...blockContent,
                        imageUrl: "",
                        altText: {},
                      })
                    }
                    className="remove-image-button"
                  >
                    Удалить
                  </button>
                </div>
              )}
            </div>

            <SimpleInput
              fieldName="altText"
              label="Альтернативный текст для изображения"
              blockContent={blockContent}
              setBlockContent={setBlockContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
            />

            <div className="form-group">
              <label htmlFor="imagePosition">Позиция изображения:</label>
              <select
                id="imagePosition"
                value={blockContent.imagePosition || "right"}
                onChange={(e) =>
                  setBlockContent({
                    ...blockContent,
                    imagePosition: e.target.value,
                  })
                }
              >
                <option value="right">Справа</option>
                <option value="left">Слева</option>
                <option value="top">Сверху</option>
                <option value="bottom">Снизу</option>
              </select>
            </div>
          </>
        );

      case "ImageBlock":
        return (
          <div className="form-group">
            <label>Изображения:</label>
            <ImageUploader
              onImageUploaded={handleImageUploaded}
              multiple={true}
            />
            {blockContent.images && blockContent.images.length > 0 && (
              <div className="current-images">
                {blockContent.images.map((image, index) => (
                  <div key={index} className="current-image">
                    <img
                      src={`${process.env.REACT_APP_API_URL}${image.imageUrl}`}
                      alt={`Image ${index + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const updatedImages = blockContent.images.filter(
                          (_, i) => i !== index
                        );
                        setBlockContent({
                          ...blockContent,
                          images: updatedImages,
                        });
                      }}
                      className="remove-image-button"
                    >
                      Удалить
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case "LinksBlock":
        return (
          <>
            <EnhancedTextInput
              fieldName="title"
              label="Заголовок блока ссылок"
              required
              blockContent={blockContent}
              setBlockContent={setBlockContent}
              currentLanguage={currentLanguage}
              setCurrentLanguage={setCurrentLanguage}
              languages={languages}
              height={150}
            />

            <div className="form-group">
              <label htmlFor="displayStyle">Стиль отображения:</label>
              <select
                id="displayStyle"
                value={blockContent.displayStyle || "default"}
                onChange={(e) =>
                  setBlockContent({
                    ...blockContent,
                    displayStyle: e.target.value,
                  })
                }
              >
                <option value="default">Обычный список</option>
                <option value="table">Таблица</option>
              </select>
            </div>

            <div className="form-group">
              <label>Ссылки:</label>
              {!blockContent.links || blockContent.links.length === 0 ? (
                <p>Нет ссылок. Добавьте новую ссылку.</p>
              ) : (
                <div className="links-list">
                  {blockContent.links.map((link, index) => (
                    <div key={index} className="link-item">
                      <div className="link-text-field">
                        <label>Текст ссылки:</label>
                        <div className="language-tabs-small">
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              type="button"
                              className={`language-tab-small ${currentLanguage === lang.code ? "active" : ""
                                }`}
                              onClick={() => setCurrentLanguage(lang.code)}
                            >
                              {lang.code.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder={`Текст ссылки на ${languages.find((l) => l.code === currentLanguage)
                              ?.name
                            }`}
                          value={link.text?.[currentLanguage] || ""}
                          onChange={(e) => {
                            const updatedLinks = [...blockContent.links];
                            if (!updatedLinks[index].text) {
                              updatedLinks[index].text = {};
                            }
                            updatedLinks[index].text[currentLanguage] =
                              e.target.value;
                            setBlockContent({
                              ...blockContent,
                              links: updatedLinks,
                            });
                          }}
                        />
                      </div>

                      <div className="link-url-field">
                        <label>URL:</label>
                        <div className="language-tabs-small">
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              type="button"
                              className={`language-tab-small ${currentLanguage === lang.code ? "active" : ""
                                }`}
                              onClick={() => setCurrentLanguage(lang.code)}
                            >
                              {lang.code.toUpperCase()}
                            </button>
                          ))}
                        </div>
                        <input
                          type="text"
                          placeholder={`URL на ${languages.find((l) => l.code === currentLanguage)
                              ?.name
                            }`}
                          value={link.url?.[currentLanguage] || ""}
                          onChange={(e) => {
                            const updatedLinks = [...blockContent.links];
                            if (!updatedLinks[index].url) {
                              updatedLinks[index].url = {};
                            }
                            updatedLinks[index].url[currentLanguage] =
                              e.target.value;
                            setBlockContent({
                              ...blockContent,
                              links: updatedLinks,
                            });
                          }}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const updatedLinks = blockContent.links.filter(
                            (_, i) => i !== index
                          );
                          setBlockContent({
                            ...blockContent,
                            links: updatedLinks,
                          });
                        }}
                        className="remove-link-btn"
                      >
                        Удалить
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => {
                  const links = blockContent.links || [];
                  setBlockContent({
                    ...blockContent,
                    links: [
                      ...links,
                      { text: { en: "", ru: "", kz: "" }, url: { en: "", ru: "", kz: "" } },
                    ],
                  });
                }}
                className="add-button-small"
              >
                Добавить ссылку
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const resetForm = () => {
    setBlockType("TextBlock");
    setBlockContent({});
    setOrder(1);
    setError("");
    setSuccess("");
    setCurrentLanguage("kz");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!pageId) {
        throw new Error("ID страницы не указан");
      }

      const blockData = {
        pageId,
        type: blockType,
        content: blockContent,
        order: parseInt(order),
      };

      console.log("Отправляемые данные:", JSON.stringify(blockData, null, 2));

      const response = await apiPost(
        `${process.env.REACT_APP_API_URL || "http://localhost:4000"}/api/block`,
        blockData
      );

      showNotification("Блок успешно добавлен", "success");
      if (onBlockAdded && typeof onBlockAdded === "function") {
        onBlockAdded(response.data);
      }
      resetForm();
    } catch (err) {
      console.error("Ошибка при добавлении блока:", err);
      showNotification(
        `Ошибка при добавлении блока: ${err.response?.data?.message || err.message
        }`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    setBlockType(e.target.value);
    setBlockContent({});
  };

  return (
    <div className="add-block-form-container">
      <h2>Добавить новый блок</h2>

      {/* Status messages are now handled by global notifications */}

      <form onSubmit={handleSubmit} className="add-block-form">
        <div className="form-group">
          <label htmlFor="blockType">Тип блока:</label>
          <select
            id="blockType"
            value={blockType}
            onChange={handleTypeChange}
            required
          >
            <option value="TitleBlock">Заголовок</option>
            <option value="TextBlock">Текстовый блок</option>
            <option value="ImageBlock">Изображения</option>
            <option value="TextImageBlock">Текст с изображением</option>
            <option value="LinksBlock">Ссылки</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="order">Порядок отображения:</label>
          <input
            type="number"
            id="order"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            min="1"
            required
          />
        </div>

        {renderContentFields()}

        <div className="form-actions">
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? "Добавление..." : "Добавить блок"}
          </button>
          <button
            type="button"
            onClick={resetForm}
            disabled={loading}
            className="reset-button"
          >
            Сбросить
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBlockForm;